import { usePortalScreen } from '../../app/telemetry/usePortalTelemetry';
import { portalScreenContext, trackPortalEvent, portalAction } from '../../app/telemetry/portalTelemetry';
import { Box, Button, Grid, Paper } from "@mui/material";
import { FormProvider, useForm } from "react-hook-form";
import EventsFormModel from "../../app/models/events/eventsFormModel";
import { yupResolver } from "@hookform/resolvers/yup";
import { EducationalAimsAndObjectivesValidationSchemas } from "./common/eventApprovalValidationSchemas";
import { useAppDispatch, useAppSelector } from "../../app/store/configureStore";
import { MapPcsEventDomainsToAutocompleteItems } from "../../app/utils/convertStaticDataToDropdownItems";
import AppMultiSelectList from "../../app/components/AppMultiSelectList";
import { useEffect, useState } from "react";
import { DropdownItem } from "../../app/models/common/dropdownItem";
import { LearningMethods } from "../../app/models/staticData/learningMethods";
import { PcsEventDto } from "../../app/models/events/pcsEventDto";
import { ApiRequestStatus } from "../../app/enums/apiRequestStatus";
import LoadingComponent from "../../app/layout/LoadingComponent";
import { API_REQUEST_FULLFILLED, MESSAGE_DETAILS_SAVED, MESSAGE_NEXT, MESSAGE_PROCESSING_DETAILS, MESSAGE_SAVE_AND_CONTINUE } from "../../app/utils/constant";
import { toast } from "react-toastify";
import { gePcsEventByIdAsync, updatePcsEventAsync } from "../../app/slices/pcsEventSlice";
import AppTextareaInput from "../../app/components/AppTextareaInput";
import { LearningMethodEnum } from "../../app/enums/learningMethodEnum";

const { LearningObjectivesOfActivity, SkillsKnowledgeAcquireDuringTheActivity, LearningTeachingMethods, OtherLearningTeachingMethods,
    EventEvaluation, MedicalCouncils8DomainsOfGoodProfessionalPractice
} = EventsFormModel.educationalAimsAndObjectivesDetailsFormField;

interface Props {
    isSpaEditabe: boolean;
    handleNext: () => void;
    handleBack: () => void;
}

export default function PcsEventEducationalDetails({ isSpaEditabe, handleNext, handleBack }: Props) {
    const methods = useForm({
        mode: 'all',
        resolver: yupResolver(EducationalAimsAndObjectivesValidationSchemas),
        defaultValues: {
            LearningTeachingMethods: ['']
        }
    });
    const formObj = methods.watch();
    const { control, reset, trigger, setValue, watch, formState: { isDirty } } = methods;

    const { learningMethods, pcsEventDomains } = useAppSelector(state => state.ReferenceData);
    const { pcsEventDetails, getPcsEventStatus, updatePcsEventStatus } = useAppSelector(state => state.pcsEvent);

    const [learningMethodsList, setLearningMethodsList] = useState<DropdownItem[]>([]);
    const [eventId,] = useState(new URLSearchParams(window.location.search).get("eventId"));

    useEffect(() => {
        if (pcsEventDetails !== null && eventId !== null && eventId !== '') {
            reset({
                [LearningObjectivesOfActivity.name]: pcsEventDetails?.learningObjectives,
                [SkillsKnowledgeAcquireDuringTheActivity.name]: pcsEventDetails?.skillsAquired,
                [LearningTeachingMethods.name]: pcsEventDetails?.learningMethodIds,
                [OtherLearningTeachingMethods.name]: pcsEventDetails?.otherLearningMethods,
                [EventEvaluation.name]: pcsEventDetails?.evaluation,
                [MedicalCouncils8DomainsOfGoodProfessionalPractice.name]: pcsEventDetails?.domains
            });
        }
    }, [reset, pcsEventDetails, eventId]);

    useEffect(() => {
        const subscription = watch((value, { name, type }) => {
            if (name === LearningTeachingMethods.name && type === 'change') {
                if (!value.LearningTeachingMethods?.includes(LearningMethodEnum.Other)) {
                    setValue(OtherLearningTeachingMethods.name as keyof PcsEventDto, '');
                }
            }
        });
        return () => subscription.unsubscribe();
    }, [setValue, watch]);

    useEffect(() => {
        if (learningMethods && learningMethods.length > 0) {
            const learningMethodList = learningMethods?.slice()
                .filter((value, index, self) => self.indexOf(value) === index);

            let learningMethodValues: DropdownItem[] = [];

            learningMethodList?.map((item: LearningMethods) => {
                learningMethodValues.push({ id: item.id, value: item.value });
                return true;
            });
            setLearningMethodsList(learningMethodValues);
        }
    }, [learningMethods]);

    const MapToPcsEvent = (values: any) => {
        const pcsEventDtoObj: PcsEventDto = {
            ...pcsEventDetails,
            learningObjectives: values.LearningObjectivesOfActivity,
            skillsAquired: values.SkillsKnowledgeAcquireDuringTheActivity,
            learningMethodIds: values.LearningTeachingMethods,
            otherLearningMethods: values.OtherLearningTeachingMethods,
            evaluation: values.EventEvaluation,
            domains: values.MedicalCouncils8DomainsOfGoodProfessionalPractice
        }
        return pcsEventDtoObj;
    }

    const dispatch = useAppDispatch();

    const saveDetails = async () => {
        const isStepValid = await trigger();
        if (!isStepValid) trackPortalEvent(portalScreenContext('PcsEventEducationalDetails'), 'ValidationBlocked', { step: 'education' });

        if (isStepValid && isDirty === true) {
            dispatch(updatePcsEventAsync(MapToPcsEvent(formObj))).then((response) => {
                if (response.meta.requestStatus.toLowerCase() === API_REQUEST_FULLFILLED) {
                    pcsEventDetails?.eventId && dispatch(gePcsEventByIdAsync(pcsEventDetails?.eventId));
                    toast.success(MESSAGE_DETAILS_SAVED);
                    handleNext();
                }
            });
        }

        if (isStepValid && isDirty === false) {
            handleNext();
        }
    }

    usePortalScreen(portalScreenContext('PcsEventEducationalDetails'), undefined, !(getPcsEventStatus === ApiRequestStatus.Pending || updatePcsEventStatus === ApiRequestStatus.Pending));

    if (getPcsEventStatus === ApiRequestStatus.Pending || updatePcsEventStatus === ApiRequestStatus.Pending) {
        return <LoadingComponent message={MESSAGE_PROCESSING_DETAILS} />
    }

    return <FormProvider {...methods}>
        <Box sx={{ minWidth: "100%" }} component={Paper}>
            <Grid container rowSpacing={4} sx={{ ml: '2%' }}>
                <Grid size={{ xs: 12 }}></Grid>

                <Grid size={{ xs: 12 }}>
                    <AppTextareaInput control={control} name={LearningObjectivesOfActivity.name}
                        maxStringCount={LearningObjectivesOfActivity.maxStringCount}
                        label={LearningObjectivesOfActivity.label} disabled={isSpaEditabe} />
                </Grid>

                <Grid size={{ xs: 12 }}>
                    <AppTextareaInput control={control} name={SkillsKnowledgeAcquireDuringTheActivity.name}
                        maxStringCount={SkillsKnowledgeAcquireDuringTheActivity.maxStringCount}
                        label={SkillsKnowledgeAcquireDuringTheActivity.label} disabled={isSpaEditabe} />
                </Grid>

                <Grid size={{ xs: 12 }}>
                    <AppMultiSelectList control={control} name={LearningTeachingMethods.name} label={LearningTeachingMethods.label}
                        items={learningMethodsList} disabled={isSpaEditabe} information_text={LearningTeachingMethods.information_text} />
                </Grid>

                {(formObj.LearningTeachingMethods as string[]).filter(x => x === LearningMethodEnum.Other).length > 0 &&
                    <Grid size={{ xs: 12 }}>
                        <AppTextareaInput control={control} name={OtherLearningTeachingMethods.name}
                            maxStringCount={OtherLearningTeachingMethods.maxStringCount}
                            label={OtherLearningTeachingMethods.label} disabled={isSpaEditabe} />
                    </Grid>}

                <Grid size={{ xs: 12 }}>
                    <AppTextareaInput control={control} name={EventEvaluation.name} label={EventEvaluation.label}
                        maxStringCount={EventEvaluation.maxStringCount} disabled={isSpaEditabe} />
                </Grid>

                <Grid size={{ xs: 12 }}>
                    <AppMultiSelectList control={control} name={MedicalCouncils8DomainsOfGoodProfessionalPractice.name}
                        label={MedicalCouncils8DomainsOfGoodProfessionalPractice.label}
                        information_text={MedicalCouncils8DomainsOfGoodProfessionalPractice.information_text}
                        items={MapPcsEventDomainsToAutocompleteItems(pcsEventDomains ?? [])} disabled={isSpaEditabe} />
                </Grid>

                <Grid size={{ xs: 5 }} sx={{ textAlign: 'start' }}>
                    <Button variant="outlined" color="primary" onClick={portalAction(portalScreenContext('PcsEventEducationalDetails'), 'PcsEventEducationalDetails.onClick.handleBack', handleBack)}>
                        Back to previous step
                    </Button>
                </Grid>

                <Grid size={{ xs: 6 }} sx={{ textAlign: 'end' }}>
                    <Button variant="contained" color="primary" onClick={saveDetails}>
                        {isDirty === true ? MESSAGE_SAVE_AND_CONTINUE : MESSAGE_NEXT}
                    </Button>
                </Grid>

                <Grid size={{ xs: 1 }}></Grid>

                <Grid size={{ xs: 12 }}></Grid>
            </Grid>
        </Box>
    </FormProvider>
}
